import {
  Property,
  ContactForm,
  ContactsResponse,
  Contact,
  ContactStats,
  Testimonial,
  TestimonialForm,
} from "./types";

const API_BASE_URL = import.meta.env.VITE_API_URL || "http://localhost:5000";
const SITE_URL = import.meta.env.VITE_SITE_URL || "http://localhost:3000";

// API error class
export class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
    this.name = "ApiError";
  }
}

// Generic API request function
async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`;

  const config: RequestInit = {
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
    credentials: "include", // Include cookies for authentication
    ...options,
  };

  try {
    const response = await fetch(url, config);

    if (!response.ok) {
      throw new ApiError(
        response.status,
        `HTTP error! status: ${response.status}`
      );
    }

    const data = await response.json();
    return data;
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }
    throw new ApiError(0, "Network error or invalid JSON response");
  }
}

// Property API functions
export const propertyApi = {
  // Get all properties
  getProperties: async (): Promise<Property[]> => {
    const backendProperties = await apiRequest<any[]>("/properties");

    // Transform backend data to frontend format
    return backendProperties.map(transformBackendProperty);
  },

  // Get a single property by ID
  getProperty: async (id: string): Promise<Property> => {
    const backendProperty = await apiRequest<any>(`/properties/${id}`);
    return transformBackendProperty(backendProperty);
  },

  // Create a new property (admin only)
  createProperty: async (
    property: Omit<Property, "id" | "createdAt" | "updatedAt">,
    imageFiles?: File[],
    videoFile?: File
  ): Promise<Property> => {
    const backendData = transformFrontendProperty(property);

    // Create FormData for file uploads
    const formData = new FormData();

    // Add property data as form fields
    Object.keys(backendData).forEach((key) => {
      formData.append(key, backendData[key].toString());
    });

    // Add image files if provided
    if (imageFiles && imageFiles.length > 0) {
      imageFiles.forEach((file) => {
        formData.append("images", file);
      });
    }

    // Add video file if provided
    if (videoFile) {
      formData.append("video", videoFile);
    }

    const url = `${API_BASE_URL}/properties`;
    const response = await fetch(url, {
      method: "POST",
      credentials: "include",
      body: formData, // Don't set Content-Type header - let browser set it with boundary
    });

    if (!response.ok) {
      throw new ApiError(
        response.status,
        `HTTP error! status: ${response.status}`
      );
    }

    const data = await response.json();
    return transformBackendProperty(data);
  },

  // Update a property (admin only)
  updateProperty: async (
    id: string,
    property: Partial<Property>,
    imageFiles?: File[],
    existingImagePaths?: string[],
    videoFile?: File,
    keepExistingVideo?: boolean
  ): Promise<Property> => {
    const backendData = transformFrontendProperty(property);

    // Create FormData for file uploads
    const formData = new FormData();

    // Add property data as form fields
    Object.keys(backendData).forEach((key) => {
      formData.append(key, backendData[key].toString());
    });

    // Always add existing image paths field to indicate user's intent
    // - If array is empty, user wants to remove all existing images
    // - If array has values, user wants to keep those specific images
    // - If field is not provided at all, backend should keep all existing images (fallback)
    if (existingImagePaths !== undefined) {
      formData.append("existing_images", existingImagePaths.join(","));
    }

    // Add new image files if provided
    if (imageFiles && imageFiles.length > 0) {
      imageFiles.forEach((file) => {
        formData.append("images", file);
      });
    }

    // Add video file if provided
    if (videoFile) {
      formData.append("video", videoFile);
    } else if (keepExistingVideo === false) {
      // Signal to remove existing video
      formData.append("video", "");
    }

    const url = `${API_BASE_URL}/properties/${id}`;
    const response = await fetch(url, {
      method: "PUT",
      credentials: "include",
      body: formData, // Don't set Content-Type header - let browser set it with boundary
    });

    if (!response.ok) {
      throw new ApiError(
        response.status,
        `HTTP error! status: ${response.status}`
      );
    }

    const data = await response.json();
    return transformBackendProperty(data);
  },

  // Delete a property (admin only)
  deleteProperty: async (id: string): Promise<void> => {
    await apiRequest(`/properties/${id}`, {
      method: "DELETE",
    });
  },
};

// Contact API functions
export const contactApi = {
  // Send contact form
  sendContactForm: async (contactData: ContactForm): Promise<void> => {
    await apiRequest("/contact", {
      method: "POST",
      body: JSON.stringify(contactData),
    });
  },

  // Send general contact form (no property required)
  sendGeneralContactForm: async (contactData: any): Promise<void> => {
    await apiRequest("/contact/general", {
      method: "POST",
      body: JSON.stringify(contactData),
    });
  },

  // Get all contacts (admin only)
  getContacts: async (
    page: number = 1,
    perPage: number = 20,
    isRead?: boolean,
    propertyId?: number
  ): Promise<ContactsResponse> => {
    const params = new URLSearchParams({
      page: page.toString(),
      per_page: perPage.toString(),
    });

    if (isRead !== undefined) {
      params.append("is_read", isRead.toString());
    }

    if (propertyId) {
      params.append("property_id", propertyId.toString());
    }

    return apiRequest(`/admin/contacts?${params.toString()}`);
  },

  // Get single contact details (admin only)
  getContact: async (id: number): Promise<Contact> => {
    return apiRequest(`/admin/contacts/${id}`);
  },

  // Get contact statistics (admin only)
  getContactStats: async (): Promise<ContactStats> => {
    return apiRequest("/admin/contacts/stats");
  },

  // Mark contact as read (admin only)
  markContactAsRead: async (
    id: number
  ): Promise<{ success: boolean; message: string }> => {
    return apiRequest(`/admin/contacts/${id}/mark-read`, {
      method: "PUT",
    });
  },

  // Delete contact (admin only)
  deleteContact: async (
    id: number
  ): Promise<{ success: boolean; message: string }> => {
    return apiRequest(`/admin/contacts/${id}`, {
      method: "DELETE",
    });
  },
};

// Authentication API functions
export const authApi = {
  // Login
  login: async (
    username: string,
    password: string
  ): Promise<{ message: string }> => {
    const url = `${API_BASE_URL}/login`;
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include", // Important for cookies
      body: JSON.stringify({ username, password }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new ApiError(response.status, errorData.error || "Login failed");
    }

    return await response.json();
  },

  // Login with Google OAuth
  googleLogin: async (credential: string): Promise<{ message: string }> => {
    const url = `${API_BASE_URL}/google-login`;
    const response = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ credential }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new ApiError(response.status, errorData.error || "Google login failed");
    }

    return await response.json();
  },

  // Logout
  logout: async (): Promise<void> => {
    const url = `${API_BASE_URL}/logout`;
    await fetch(url, {
      method: "GET",
      credentials: "include",
    });
  },

  // Check if user is authenticated
  checkAuth: async (): Promise<{ authenticated: boolean }> => {
    const url = `${API_BASE_URL}/login`;
    try {
      const response = await fetch(url, {
        method: "GET",
        credentials: "include",
      });

      if (response.ok) {
        return { authenticated: true };
      } else {
        return { authenticated: false };
      }
    } catch (error) {
      return { authenticated: false };
    }
  },
};

// Transform backend property data to frontend format
function transformBackendProperty(backendProperty: any): Property {
  const images = backendProperty.images || [];
  const imageUrls = Array.isArray(images)
    ? images.map((img: any) => `${SITE_URL}/${img.path || img}`)
    : [];

  const video = backendProperty.video;
  const videoUrl = video ? `${SITE_URL}/${video.path || video}` : undefined;

  return {
    id: backendProperty.id.toString(),
    title: {
      en: backendProperty.free_text_en || "",
      he: backendProperty.free_text_he || "",
      fr: backendProperty.free_text_fr || backendProperty.free_text_en || "",
    },
    description: {
      en: backendProperty.free_text_en || "",
      he: backendProperty.free_text_he || "",
      fr: backendProperty.free_text_fr || backendProperty.free_text_en || "",
    },
    images: imageUrls,
    video: videoUrl,
    location: {
      city: {
        en: backendProperty.city_en || "Netanya",
        he: backendProperty.city_he || "נתניה",
        fr: backendProperty.city_fr || "Netanya",
      },
      neighborhood: {
        en: backendProperty.neighborhood_en || "Center",
        he: backendProperty.neighborhood_he || "מרכז",
        fr: backendProperty.neighborhood_fr || "Centre",
      },
      address: {
        en: `${backendProperty.neighborhood_en || "Center"}, ${
          backendProperty.city_en || "Netanya"
        }, Israel`,
        he: `${backendProperty.neighborhood_he || "מרכז"}, ${
          backendProperty.city_he || "נתניה"
        }, ישראל`,
        fr: `${backendProperty.neighborhood_fr || "Centre"}, ${
          backendProperty.city_fr || "Netanya"
        }, Israël`,
      },
    },
    price: backendProperty.price || 0,
    currency: "ILS",
    rooms: backendProperty.rooms || 0,
    bedrooms: backendProperty.bedrooms || backendProperty.rooms || 0,
    bathrooms: backendProperty.bathrooms || 1,
    size: backendProperty.size || 0,
    type: backendProperty.action === "sell" ? "sale" : "rent",
    status: backendProperty.status ? "available" : "sold",
    features: [], // Add features based on your backend data if needed
    createdAt:
      backendProperty.createdAt ||
      backendProperty.created_at ||
      new Date().toISOString(),
    updatedAt:
      backendProperty.updatedAt ||
      backendProperty.updated_at ||
      new Date().toISOString(),
  };
}

// Transform frontend property data to backend format
function transformFrontendProperty(frontendProperty: Partial<Property>): any {
  return {
    action: frontendProperty.type === "sale" ? "sell" : "rent",
    status: frontendProperty.status === "available",
    free_text_en:
      frontendProperty.title?.en || frontendProperty.description?.en || "",
    free_text_he:
      frontendProperty.title?.he || frontendProperty.description?.he || "",
    free_text_fr:
      frontendProperty.title?.fr || frontendProperty.description?.fr || "",
    city_en: frontendProperty.location?.city?.en || "",
    city_he: frontendProperty.location?.city?.he || "",
    city_fr: frontendProperty.location?.city?.fr || "",
    neighborhood_en: frontendProperty.location?.neighborhood?.en || "",
    neighborhood_he: frontendProperty.location?.neighborhood?.he || "",
    neighborhood_fr: frontendProperty.location?.neighborhood?.fr || "",
    rooms: frontendProperty.rooms || 0,
    bedrooms: frontendProperty.bedrooms ?? frontendProperty.rooms ?? 0,
    bathrooms: frontendProperty.bathrooms ?? 1,
    size: frontendProperty.size || 0,
    price: frontendProperty.price || 0,
  };
}

// Utility function to extract file paths from image URLs
export function extractImagePaths(imageUrls: string[]): string[] {
  return imageUrls
    .map((url) => {
      try {
        // Extract the path after the API_BASE_URL
        // URLs are in format: http://localhost:5000/images/filename.jpg
        // We want to extract: images/filename.jpg
        const urlObj = new URL(url);
        const path = urlObj.pathname.startsWith("/")
          ? urlObj.pathname.slice(1)
          : urlObj.pathname;
        return path;
      } catch (error) {
        return null;
      }
    })
    .filter((path): path is string => path !== null && path !== "");
}

// Testimonial API functions
export const testimonialApi = {
  // Get all approved testimonials
  getApprovedTestimonials: async (): Promise<Testimonial[]> => {
    return await apiRequest<Testimonial[]>("/testimonials/approved");
  },

  // Get all testimonials (admin only)
  getAllTestimonials: async (): Promise<Testimonial[]> => {
    return await apiRequest<Testimonial[]>("/testimonials");
  },

  // Submit a new testimonial
  submitTestimonial: async (testimonial: TestimonialForm): Promise<void> => {
    await apiRequest("/testimonials", {
      method: "POST",
      body: JSON.stringify(testimonial),
    });
  },

  // Update testimonial status (admin only)
  updateTestimonialStatus: async (
    id: string,
    status: "approved" | "rejected"
  ): Promise<Testimonial> => {
    return await apiRequest<Testimonial>(`/testimonials/${id}/status`, {
      method: "PUT",
      body: JSON.stringify({ status }),
    });
  },

  // Delete testimonial (admin only)
  deleteTestimonial: async (id: string): Promise<void> => {
    await apiRequest(`/testimonials/${id}`, {
      method: "DELETE",
    });
  },
};
